import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { gqlRequest } from "../lib/gql";
import { REGION_DETAIL, type RegionDetailResult } from "../lib/queries";
import { Map, MapPin } from "lucide-react";
import { PLACE_TYPE_ICONS } from "../lib/format";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { ReportButton } from "../components/ReportButton";
import { ViewOnMapButton } from "../components/ViewOnMapButton";
import { EmptyState, ErrorBox, Select, Spinner } from "../components/ui";
import { MiniMap } from "../map/MiniMap";
import { toFeature } from "../lib/geo";

type PlaceSort = "name" | "elevation";

export function RegionPage({ id }: { id: string }) {
  const { t, labelizeT, entityName } = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ["region", id],
    queryFn: () => gqlRequest<RegionDetailResult>(REGION_DETAIL, { id }),
  });
  const [placeSort, setPlaceSort] = useState<PlaceSort>("name");

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const region = data?.region;
  if (!region) return <EmptyState>{t("region.notFound")}</EmptyState>;

  const header = (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">{t("region.kicker")} · {labelizeT(region.type)}</p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{entityName(region)}</h1>
        <div className="flex items-center gap-2">
          <ViewOnMapButton geometry={region.geomSimple} />
          <ReportButton entityKind="REGION" entityId={region.id} />
        </div>
      </div>
      {region.parent && (
        <p className="mt-1 text-sm text-muted-foreground">
          {t("region.partOf")}{" "}
          <Link
            href={`/region/${region.parent.id}`}
            className="text-primary transition-colors hover:underline"
          >
            {entityName(region.parent)}
          </Link>
        </p>
      )}
    </div>
  );

  const infoTab = (
    <MiniMap
      features={[toFeature(region.geomSimple, { id: region.id })]}
      className="h-96 w-full overflow-hidden rounded-xl border shadow-card"
    />
  );

  const subregions = region.regionsByParentId.nodes;
  const subregionsTab = subregions.length ? (
    <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
      {subregions.map((r) => (
        <li key={r.id}>
          <Link
            href={`/region/${r.id}`}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
          >
            <Map aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">
              <span className="block font-medium text-foreground">{entityName(r)}</span>
              <span className="block text-xs text-muted-foreground">{labelizeT(r.type)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  ) : (
    <EmptyState>{t("region.noSubregions")}</EmptyState>
  );

  // Client-side sort over the fetched window (first: 100) — with more places
  // than that, the sort covers only the loaded page.
  const places = [...region.placeRegionsByRegionId.nodes];
  if (placeSort === "name") places.sort((a, b) => a.place.name.localeCompare(b.place.name));
  // `|| 0`: both-null comparisons yield -Infinity - -Infinity = NaN.
  else places.sort((a, b) => ((b.place.elevation ?? -Infinity) - (a.place.elevation ?? -Infinity)) || 0);
  const placesTab = places.length ? (
    <div className="space-y-3">
      <label className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
        {t("common.sort")}
        <Select className="!w-auto" value={placeSort} onChange={(e) => setPlaceSort(e.target.value as PlaceSort)}>
          <option value="name">{t("common.name")}</option>
          <option value="elevation">{t("region.sortElevation")}</option>
        </Select>
      </label>
    <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
      {places.map(({ place }) => {
        const PlaceIcon = PLACE_TYPE_ICONS[place.type] ?? MapPin;
        return (
        <li key={place.id}>
          <Link
            href={`/place/${place.id}`}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
          >
            <PlaceIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">
              <span className="block font-medium text-foreground">{entityName(place)}</span>
              <span className="block text-xs text-muted-foreground">
                {labelizeT(place.type)}
                {place.elevation != null ? ` · ${place.elevation} m` : ""}
              </span>
            </span>
          </Link>
        </li>
        );
      })}
    </ul>
    </div>
  ) : (
    <EmptyState>{t("region.noPlaces")}</EmptyState>
  );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "info", label: t("tabs.map"), content: infoTab },
        { key: "places", label: t("tabs.places"), badge: region.placeRegionsByRegionId.totalCount, content: placesTab },
        { key: "subregions", label: t("tabs.subregions"), badge: subregions.length, content: subregionsTab },
      ]}
    />
  );
}
